import { Pause, Play } from '@audio-network/componentlibrary/icons'
import { PlaceholderAlbumArtwork, Tag } from '@audio-network/componentlibrary'
import React, { memo, useEffect, useRef, useState } from 'react'
import { SelectNumber, TrackActions } from './components'
import { getComposerUrl, getTrackUrl } from '@/utils/urls'

import Image from 'next/image'
import { MusicAppLink } from '@/utils/shouldLinkToMusicApp'
import { TRACK_ROW_THUMBNAIL_WIDTH } from '@/constants/sizing'
import { TrackRowProps } from './TrackRow.types'
import clsx from 'clsx'
import formatDuration from '@/utils/formatting/formatDuration'
import { hasStemsAccess } from '@/utils/hasStemsAccess'
import optimizeContentfulImage from '@/utils/optimizeContentfulImage'
import { sendAnalytics } from '@audio-network/analytics'
import styles from './TrackRow.module.css'
import { usePlayer } from '@audio-network/player'
import { useSession } from '@/context/Session'
import { useTranslation } from 'next-i18next'

const TrackRow = ({
	rowNumber,
	track,
	albumArtworkUrl,
	fetchingAlbumArtworkUrl,
	source,
	onDelete,
	onMoveToBottom,
	onMoveToTop,
	onChangeNotes,
	notes,
	showSelect,
	setTrackSelected,
	handleChecked,
	...props
}: TrackRowProps): JSX.Element => {
	const { t } = useTranslation(['trackrow'])
	const { user } = useSession()
	const [artworkUrl, setArtworkUrl] = useState(null)
	const [showPlaceholderAlbumArtwork, setShowPlaceholderAlbumArtwork] =
		useState(false)
	const [showDeleteAnimation, setShowDeleteAnimation] = useState(false)
	const trackRowWrapperRef = useRef(null)

	const {
		togglePlay,
		isPlaying: isPlayingFromPlayer,
		currentTrack,
		playHistory,
	} = usePlayer()

	const currentPlayingTrack = (): boolean => {
		return isPlayingFromPlayer && currentTrack?.id === track.id
	}

	const trackPageURL = getTrackUrl(track)

	const handleClick = (element: string): void => {
		sendAnalytics({
			noun: element,
			pastTenseVerb: 'Clicked',
			eventParameters: {
				TrackId: track.id,
				TrackTitle: track.title,
				TrackMixCount: track.mixCount,
				TrackHasStems: track.hasStems,
				TrackSource: source,
			},
		})
	}

	// album artwork fetch
	useEffect(() => {
		if (albumArtworkUrl) {
			setArtworkUrl(albumArtworkUrl)
		} else if (!fetchingAlbumArtworkUrl) {
			setShowPlaceholderAlbumArtwork(true)
		}
	}, [albumArtworkUrl, fetchingAlbumArtworkUrl])

	// displaying info
	const getComposerLastNames = (composers: Composer[]): string => {
		const composerNames: string[] = []
		composers.map((composer) => {
			composerNames.push(composer.lastName)
		})

		return composerNames.join(' , ').replace(/,/g, '/')
	}

	// menu action
	const handleDelete = (): void => {
		trackRowWrapperRef.current.addEventListener(
			'transitionend',
			(event: TransitionEvent) => {
				// Don't respond to child's transitions (flyout menu)
				if (event.target !== trackRowWrapperRef.current) {
					return
				}

				// Don't act on fade out
				if (event.propertyName === 'opacity') {
					return
				}

				// Instead wait for the (delayed) shrink
				onDelete(track)
			}
		)

		setShowDeleteAnimation(true)
	}

	const selected = handleChecked && handleChecked(track)

	return (
		<div
			className={clsx(
				styles.trackRowrapper,
				showDeleteAnimation && styles.showDeleteAnimation
			)}
			{...props}
			ref={trackRowWrapperRef}
		>
			<article
				className={clsx(
					styles.trackRow,
					playHistory.includes(track.id) && styles.previouslyPlayed,
					rowNumber && styles.showRowNumber,
					// Still catering for browsers that don't support :has
					setTrackSelected && styles.hasSelect,
					showSelect && styles.hideTrackNumber
				)}
			>
				{setTrackSelected && (
					<SelectNumber
						selectAlwaysVisible={showSelect}
						setTrackSelected={setTrackSelected}
						track={track}
						selected={selected}
					/>
				)}
				<div className={styles.artwork}>
					{artworkUrl && (
						<Image
							data-testid={`albumartwork-${track.id}`}
							alt=""
							src={optimizeContentfulImage(artworkUrl, {
								width: TRACK_ROW_THUMBNAIL_WIDTH,
								height: TRACK_ROW_THUMBNAIL_WIDTH,
							})}
							height={TRACK_ROW_THUMBNAIL_WIDTH}
							width={TRACK_ROW_THUMBNAIL_WIDTH}
						/>
					)}
					{showPlaceholderAlbumArtwork && <PlaceholderAlbumArtwork />}
				</div>
				<button
					className={clsx(styles.playButton, 'play', 'track')}
					aria-label={
						currentPlayingTrack()
							? `Pause ${track.title}`
							: `Play ${track.title}`
					}
					onClick={() => {
						togglePlay(track, { source: source })
					}}
					data-tracksource={source}
					data-testid={`track-row-${track.id}`}
					data-composer={getComposerLastNames(track.composers)}
					data-duration={track.duration}
					data-album={track.album.number}
					data-trackid={track.id}
					data-trackanwref={`${track.album.number}/${track.albumTrackNumber}`}
					data-tracktitle={track.title}
				>
					{currentPlayingTrack() ? (
						<Pause className={styles.playIcon} />
					) : (
						<Play className={styles.playIcon} />
					)}
					<div className={styles.details}>
						<span className={styles.title}>{track.title}</span>
						<span
							className={styles.albumNumber}
						>{`${track.album.number}/${track.albumTrackNumber}`}</span>
						<div className={styles.composers}>
							{track.originalComposer && (
								<span className={styles.composer}>
									{`${track.originalComposer} - Arr. `}
								</span>
							)}
							{track.composers.map((composer, index) => (
								<React.Fragment key={composer.name}>
									<span className={styles.composer}>
										{composer.name} ({composer.collectionSociety})
									</span>
									{index < track.composers.length - 1 && ' | '}
								</React.Fragment>
							))}
						</div>
					</div>
				</button>
				<div className={styles.details}>
					<MusicAppLink
						className={styles.title}
						href={trackPageURL}
						onClick={() => handleClick('Track')}
						data-testid="track-row-title"
					>
						{track.title}
					</MusicAppLink>
					<span
						className={styles.albumNumber}
						data-testid="track-row-track-number"
					>{`${track.album.number}/${track.albumTrackNumber}`}</span>
					<div className={styles.composers} data-testid="track-row-artists">
						{track.originalComposer && (
							<span className={styles.composer}>
								{`${track.originalComposer} - Arr. `}
							</span>
						)}
						{track.composers.map((composer, index) => (
							<React.Fragment key={composer.name}>
								<MusicAppLink
									className={styles.composer}
									href={getComposerUrl(composer)}
								>
									{composer.name} ({composer.collectionSociety})
								</MusicAppLink>
								{index < track.composers.length - 1 && ' | '}
							</React.Fragment>
						))}
					</div>
					<div className={styles.tags}>
						{track.explicit && (
							<Tag
								data-testid="track-row-explicit"
								label="E"
								title="Explicit"
								color="ui4"
								round
							/>
						)}
						{track.mixCount >= 2 && (
							<MusicAppLink href={trackPageURL} elseFragment>
								<Tag
									label={t('mixes', { mixCount: track.mixCount })}
									href={trackPageURL}
									color="backgroundSecondary"
									onClick={() => handleClick('Mixes Tag')}
								/>
							</MusicAppLink>
						)}
						{hasStemsAccess(user) && track.hasStems && (
							<MusicAppLink
								href={`${trackPageURL}?initialTab=stems`}
								elseFragment
							>
								<Tag
									label={t('stems')}
									href={`${trackPageURL}?initialTab=stems`}
									color="backgroundSecondary"
									onClick={() => handleClick('Stems Tag')}
								/>
							</MusicAppLink>
						)}
					</div>
					<span
						className={styles.description}
						data-testid="track-row-description"
					>
						{track.description}
					</span>
					<span className={styles.duration} data-testid="track-row-duration">
						{formatDuration(track.duration)}
					</span>
				</div>
				{!showSelect && (
					<TrackActions
						notes={notes}
						track={track}
						source={source}
						onChangeNotes={onChangeNotes}
						onMoveToTop={onMoveToTop}
						onMoveToBottom={onMoveToBottom}
						onDelete={onDelete}
						handleDelete={handleDelete}
					/>
				)}
			</article>
		</div>
	)
}

export default memo(TrackRow)
